import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { ICreateSubjectError } from '../../interface/subjectModel';
import axios, { AxiosError } from 'axios';
import api from '../../services/api';
import {
  ICommentResponse,
  ICommentResponseBody,
  ICreatePost,
  IPostEditResponse,
  IPostCreateResponse,
  ILikesResponse,
  IPostDeleteResponseBody,
  IVisibilityResponse,
  IPostQuery,
  IPostListResponse,
  ICommentsResponse,
} from '../../interface/postModel';
import {
  commentPost,
  deleteComment,
  deletePost,
  fetchComment,
  getPost,
  postCreate,
  postEdit,
  postVisibility,
  getCommentsForPost,
} from '../../services/post';

export const createPost = createAsyncThunk<
  IPostCreateResponse,
  ICreatePost,
  { rejectValue: ICreateSubjectError }
>('post/create', async (body, { rejectWithValue }) => {
  try {
    const response = await postCreate(body);
    return response;
  } catch (err: any) {
    // HTTP 413 is surfaced by the multipart interceptor as { status: 413, message }.
    // Handle it explicitly so a friendly upload-too-large message reaches the UI.
    if (err?.status === 413 || err?.response?.status === 413) {
      return rejectWithValue({
        status: 413,
        message: err?.message || i18n.t('post.upload_too_large'),
      });
    }
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});

export const toggleLike = createAsyncThunk<void, string, { rejectValue: ICreateSubjectError }>(
  'post/like',
  async (body, { rejectWithValue }) => {
    try {
      await api.post(`/post/like`, { post_template_id: body });
      // No return value needed - like state is managed optimistically
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const error = err as AxiosError<ICreateSubjectError>;
        return rejectWithValue({
          status: error.response?.status || 500,
          message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
        });
      } else {
        return rejectWithValue({ status: err?.status, message: err.message });
      }
    }
  },
);

export const fetchLikeById = createAsyncThunk<
  ILikesResponse,
  string,
  { rejectValue: ICreateSubjectError }
>('post/fetch-like-by-id', async (body, { rejectWithValue }) => {
  try {
    await api.get(`/post/count/${body}`);
    //return response.data ;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});

export const fetchVisibility = createAsyncThunk<
  IVisibilityResponse,
  void,
  { rejectValue: ICreateSubjectError }
>('post/fetch-visibility', async (_, { rejectWithValue }) => {
  try {
    const response = await postVisibility();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});

export const postComment = createAsyncThunk<
  ICommentResponseBody,
  { body: string; input: string },
  { rejectValue: ICreateSubjectError }
>('post/post-comment', async ({ body, input }, { rejectWithValue }) => {
  try {
    const response = await commentPost({
      body,
      input,
    });
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});

export const fetchCommentById = createAsyncThunk<
  ICommentResponse,
  string,
  { rejectValue: ICreateSubjectError }
>('post/fetch-comment', async (body, { rejectWithValue }) => {
  try {
    const response = await fetchComment(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});

export const deleteCommentById = createAsyncThunk<
  IPostDeleteResponseBody,
  string,
  { rejectValue: ICreateSubjectError }
>('post/delete-comment', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteComment(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});

export const editPost = createAsyncThunk<
  IPostEditResponse,
  FormData,
  { rejectValue: ICreateSubjectError }
>('post/edit', async (body, { rejectWithValue }) => {
  try {
    const response = await postEdit(body);
    return response;
  } catch (err: any) {
    // HTTP 413 is surfaced by the multipart interceptor as { status: 413, message }.
    // Handle it explicitly so a friendly upload-too-large message reaches the UI.
    if (err?.status === 413 || err?.response?.status === 413) {
      return rejectWithValue({
        status: 413,
        message: err?.message || i18n.t('post.upload_too_large'),
      });
    }
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});

// ==================== Pagination Actions ====================

/**
 * Fetches paginated posts (initial load or refresh)
 */
export const fetchPostPaginated = createAsyncThunk<
  IPostListResponse,
  IPostQuery,
  { rejectValue: ICreateSubjectError }
>('post/fetchPaginated', async (query, { rejectWithValue }) => {
  try {
    const response = await getPost(query);
    return response;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_posts', 'Failed to fetch posts'),
      });
    }
    const genericError = err as { status?: number; message?: string };
    return rejectWithValue({
      status: genericError?.status || 500,
      message: genericError?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetches more posts for infinite scroll (appends to existing)
 */
export const fetchMorePosts = createAsyncThunk<
  IPostListResponse,
  IPostQuery,
  { rejectValue: ICreateSubjectError }
>('post/fetchMore', async (query, { rejectWithValue }) => {
  try {
    const response = await getPost(query);
    return response;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_more_posts', 'Failed to fetch more posts'),
      });
    }
    const genericError = err as { status?: number; message?: string };
    return rejectWithValue({
      status: genericError?.status || 500,
      message: genericError?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetches paginated comments for a post
 */
export const fetchPostCommentsPaginated = createAsyncThunk<
  ICommentsResponse & { postId: string },
  { postId: string; page: number; pageSize: number },
  { rejectValue: ICreateSubjectError }
>('post/fetchCommentsPaginated', async ({ postId, page, pageSize }, { rejectWithValue }) => {
  try {
    const response = await getCommentsForPost(postId, page, pageSize);

    const payload = { ...response, postId };

    return payload;
  } catch (err: unknown) {
    console.error('❌ Error in fetchPostCommentsPaginated:', err);
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_comments', 'Failed to fetch comments'),
      });
    }
    const genericError = err as { status?: number; message?: string };
    return rejectWithValue({
      status: genericError?.status || 500,
      message: genericError?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const deletePostThunk = createAsyncThunk<
  IPostDeleteResponseBody,
  string,
  { rejectValue: ICreateSubjectError }
>('post/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deletePost(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err?.status, message: err.message });
    }
  }
});
